import { FC } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Box } from '@mui/material';
import { FixedRuleForm } from './FixedRuleForm';
import { PaperComponent } from 'shared/ui/draggable/Draggable';
import { useTranslation } from 'react-i18next';
import { AlertCustom } from 'shared/ui/alert/AlertCustom';
import { FormDelete } from 'shared/ui/form/FormDelete';
import CloseIcon from '@mui/icons-material/Close';
import { FixedRule } from 'repository/timekeeper/@types/FixedRuleModel';
import { useFixRuleForm } from 'hooks/fixRules/useFixRuleForm';

type Props = {
    open: boolean;
    rows: FixedRule[];
    selectedRow: FixedRule[];
    reloading: () => void;
    mode: 'add' | 'edit' | 'delete' | 'info';
    setOpen: (open: boolean) => void;
    cleanSelected: () => void;
};

export const FixRuleDialog: FC<Props> = ({ open, mode, rows, selectedRow, reloading, setOpen, cleanSelected }) => {
    const { t } = useTranslation();
    const form = useFixRuleForm({ rows, reloading, mode, selectedRow, open, setOpen, cleanSelected });

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                if (mode === 'delete' || mode === 'info') {
                    form.cleanForm();
                } else {
                    form.detectChanges();
                }
            }}
            PaperComponent={PaperComponent}
            maxWidth="sm"
            fullWidth
            aria-labelledby="draggable-dialog-title"
            aria-describedby="draggable-alert-dialog-description"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {mode === 'edit' ? t('settings.fixed_rules.dialog.titles.edit') : t('settings.fixed_rules.dialog.titles.add')}
            </DialogTitle>

            <IconButton
                aria-label="close"
                onClick={() => {
                    if (mode === 'delete' || mode === 'info') {
                        form.cleanForm();
                    } else {
                        form.detectChanges();
                    }
                }}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>

            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>{form.alert && <AlertCustom alerts={form.alert} setAlerts={form.setAlert} closeForm={form.cleanForm} t={t} />}</Box>

            <DialogContent dividers>
                {mode === 'delete' ? (
                    <FormDelete
                        open={open}
                        title={t('settings.fixed_rules.dialog.titles.delete')}
                        description={t('settings.fixed_rules.dialog.alerts.delete.message')}
                        names={selectedRow.map((row) => row.name)}
                        onCancel={form.cleanForm}
                        onConfirm={form.handleSubmit}
                        textButton={t('general.buttons.delete')}
                        mode="delete"
                    />
                ) : (
                    <FixedRuleForm form={form} mode={mode} />
                )}
            </DialogContent>

            {mode !== 'delete' && mode !== 'info' && (
                <DialogActions>
                    <Button variant="contained" color="error" onClick={form.cleanForm}>
                        {t('general.buttons.cancel')}
                    </Button>
                    <Button variant="contained" color="primary" onClick={form.handleSubmit} disabled={form.disabled}>
                        {' '}
                        {t('general.buttons.save')}
                    </Button>
                </DialogActions>
            )}

            {mode === 'info' && (
                <DialogActions>
                    <Button variant="contained" color="error" onClick={form.cleanForm}>
                        {t('general.buttons.close')}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};
